import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { PendingApprovalJob } from '@app/models/job.model';
import { JobService } from '@app/services/job/job.service';
import { NavigationService } from '@app/services/navigation/navigation.service';

@Component({
  selector: 'app-pending-approval-jobs-list',
  templateUrl: './pending-approval-jobs-list.component.html',
  styleUrl: './pending-approval-jobs-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PendingApprovalJobsListComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly navigationService = inject(NavigationService);
  private readonly jobService = inject(JobService);
  private readonly jobTypeParam = this.route.snapshot.queryParamMap.get('jobType');
  private readonly jobType = this.jobTypeParam === 'PPM' ? 'PPM' : 'REACTIVE';
  readonly siteId = this.route.snapshot.queryParamMap.get('siteId') ?? '';
  readonly siteName = this.route.snapshot.queryParamMap.get('siteName') ?? '';
  readonly listJobType = this.jobTypeParam === 'PPM' ? 'PPM' : 'New';
  private readonly hasValidRequestParams =
    !!this.siteId && (this.jobTypeParam === 'New' || this.jobTypeParam === 'PPM');
  readonly titleKey = this.jobType === 'PPM' ? 'pending_approval_list_ppm' : 'pending_approval_list_jobs';
  readonly jobs = signal<PendingApprovalJob[]>([]);
  readonly isLoading = signal(true);
  readonly hasLoadError = signal(false);

  constructor() {
    this.navigationService.onChange(false, false);
  }

  async ngOnInit(): Promise<void> {
    if (!this.hasValidRequestParams) {
      this.hasLoadError.set(true);
      this.isLoading.set(false);
      return;
    }

    try {
      const jobs = await firstValueFrom(this.jobService.getJobsPendingApproval());
      this.jobs.set((jobs ?? []).filter(job =>
        job.siteId.toLowerCase() === this.siteId.toLowerCase() &&
        job.jobType?.toUpperCase() === this.jobType
      ));
    } catch {
      this.hasLoadError.set(true);
    } finally {
      this.isLoading.set(false);
    }
  }

  ngOnDestroy(): void {
    this.navigationService.onChange(true, false);
  }
}
